import * as React from 'react';
import { connect } from 'react-redux';
import WKTParser from 'terraformer-wkt-parser';
import * as THREE from 'three';
import datafangstHelpers from '../../../../utilities';
import { changeSceneData, deSelectFeatures, selectFeatures } from '../../../actions/actions';
import FeaturesState, { splitDirection } from '../../contract-features-3/domain/FeaturesState';

import {
    DisplayFeature,
    DisplayFeatureType,
    Ground3dData,
    SceneData
} from '../../contract-features-3/domain';
import ColorHandler from '../ColorHandler';
import { Bounds, getBounds } from '../utils/bounds';
import FeatureStyler from '../utils/FeatureStyler';
import Options from './Options';
import { OrbitControls } from './OrbitControls';
import SceneHandler from './SceneHandler';
import {GeometryObject} from "geojson";
import {FeatureTypeSummary} from "../../../../integration/representation/feature-type";

interface Render3dProps {
    featureTypes: FeatureTypeSummary[],
    additionalSelectedFeatureTypes: DisplayFeatureType[],
    selectedFeatures: string[],
    selectedFeatureType: DisplayFeatureType,
    splitScreenDirection: splitDirection,
    deSelectFeatures: Function,
    selectFeatures: Function,
    changeSceneData: Function,
    sceneData: SceneData,
    ground3dData: Ground3dData
}

interface Render3dState {
    featureTypeColors?: { [id: number]: string },
    systemCenter?: [number, number]
}

class Render3d extends React.Component<Render3dProps, Render3dState> {

    private readonly COLOR_HANDLER = new ColorHandler(true);
    private readonly FOV = 50;

    private renderHeight: number;
    private renderWidth: number;
    private raycaster: THREE.Raycaster;
    private mouse: THREE.Vector2;

    private boundingBox: Bounds;

    private currentIntersected: THREE.Object3D;

    constructor(props: Render3dProps) {
        super(props);

        this.state = {
            featureTypeColors: null,
            systemCenter: null
        };
    }

    /**
     * Called when:
     * 1. The page (/3d) is first loaded.
     * 2. The split direction is changed (horizontal/vertical).
     *
     * Responsible for:
     * 1. Gathering information about current canvas.
     * 2. Adjusting camera to new canvas.
     * 3. Creating extras and helpers for the scene each time it's mounted.
     * 4. Creating all the initial data needed to present the 3D scene if it hasn't been already.
     * 5. Binding the scene and renderer to the new canvas.
     */
    componentDidMount() {
        this.updateInformationAboutCanvas();
        this.updateCameraAndRendererToNewCanvasSize();

        this.createSceneExtras();

        if (this.props.sceneData == undefined) {
            this.createFirstTimeScene();
        }

        this.bindSceneToCanvas();
    }

    componentWillReceiveProps(nextProps: Render3dProps) {
        if (nextProps.selectedFeatureType != undefined) {
            if (this.state.featureTypeColors == undefined) {
                this.assignColorsToFeatureTypes(nextProps);
            }

            this.bindSceneToCanvas(nextProps);

            this.findBoundingBox(nextProps);
            this.setSystemCenter();

            // New primary feature type selected
            if (nextProps.selectedFeatureType !== this.props.selectedFeatureType) {
                this.zoomToBoundingBox();
            }

            // New feature selected
            if (nextProps.selectedFeatures !== this.props.selectedFeatures) {
                const newlySelected = nextProps.selectedFeatures.filter((f) => !(new Set(this.props.selectedFeatures)).has(f));
                for (let featureId of newlySelected) {
                    for (let feature of nextProps.sceneData.scene.children.filter((c) => c.name === featureId)) {
                        FeatureStyler.styleSelected(feature as THREE.Mesh);
                    }
                }

                const newlyDeselected = this.props.selectedFeatures.filter((f) => !(new Set(nextProps.selectedFeatures)).has(f));
                for (let featureId of newlyDeselected) {
                    for (let feature of nextProps.sceneData.scene.children.filter((c) => c.name === featureId)) {
                        FeatureStyler.styleUnselected(feature as THREE.Mesh);
                    }
                }
            }
        }

        requestAnimationFrame(this.animateScene);
    }

    render() {
        let dependentComponents = null;
        if (this.props.sceneData != undefined) {
            dependentComponents = (
                <SceneHandler
                    featureTypeColors={this.state.featureTypeColors}
                    systemCenter={this.state.systemCenter}/>
            );
        }

        return (
            <div>
                <div ref='render-3d'/>

                <div ref='axis-helper' className='axis-helper'/>

                {dependentComponents}

                <Options/>
            </div>
        );
    }

    private updateInformationAboutCanvas() {
        const container = (this.refs['render-3d'] as HTMLElement).parentElement.parentElement;

        this.renderHeight = container.clientHeight;
        this.renderWidth = container.clientWidth;
    }

    private createSceneExtras() {
        // Create raycaster and mouse vector for interaction with objects
        this.raycaster = new THREE.Raycaster();
        this.raycaster.linePrecision = 1;
        this.mouse = new THREE.Vector2();
    }

    private createFirstTimeScene() {
        // Create scene
        const scene = new THREE.Scene();

        // Create renderer
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(this.renderWidth, this.renderHeight);
        renderer.setClearColor(0xeeeeee, 1);

        // Create camera
        const camera = new THREE.PerspectiveCamera(this.FOV, this.renderWidth/this.renderHeight, 0.01, 100000000);
        camera.updateProjectionMatrix();

        // Create controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableKeys = false;
        controls.addEventListener('change', () => requestAnimationFrame(this.animateScene));

        // Create axis helper
        const axisHelperScene = new THREE.Scene();
        const axisHelperRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        axisHelperRenderer.setSize(200, 200);
        const axisHelperCamera = new THREE.PerspectiveCamera(this.FOV, 200/200, 1, 1000);
        axisHelperCamera.up = camera.up;
        // Add axes to axis helper
        const axes: THREE.AxisHelper = new THREE.AxisHelper(100);
        axes.rotateX(datafangstHelpers.degreesToRadians(-90));
        axisHelperScene.add(axes);

        // Add lights
        const directionalLight: THREE.DirectionalLight = new THREE.DirectionalLight(0xffffff);
        directionalLight.position.set(1, 1, 1);
        scene.add(directionalLight);
        const directionalLight2: THREE.DirectionalLight = new THREE.DirectionalLight(0x002288);
        directionalLight2.position.set(-1, -1, -1);
        scene.add(directionalLight2);
        const ambientLight: THREE.AmbientLight = new THREE.AmbientLight(0x222222);
        scene.add(ambientLight);

        // Add intersection sphere
        const geometry = new THREE.SphereGeometry(0.5, 16, 12);
        const material = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.5
        });
        const intersectionSphere = new THREE.Mesh(geometry, material);
        intersectionSphere.visible = false;
        intersectionSphere.name = 'intersectionSphere';
        scene.add(intersectionSphere);

        // Update state
        const axisHelperData = {
            scene: axisHelperScene,
            renderer: axisHelperRenderer,
            camera: axisHelperCamera
        };
        this.props.changeSceneData(new SceneData(scene, renderer, camera, controls, axisHelperData));
    }

    private bindSceneToCanvas(props = this.props) {
        const renderContainer = (this.refs['render-3d'] as HTMLElement);
        while (renderContainer.firstChild) {
            renderContainer.removeChild(renderContainer.firstChild);
        }

        const axisHelperContainer = (this.refs['axis-helper'] as HTMLElement);
        while (axisHelperContainer.firstChild) {
            axisHelperContainer.removeChild(axisHelperContainer.firstChild);
        }

        // Bind renderers
        if (props.sceneData != undefined) {
            renderContainer.appendChild(props.sceneData.renderer.domElement);
            axisHelperContainer.appendChild(props.sceneData.axisHelper.renderer.domElement);
        }

        // Bind listeners
        window.addEventListener('resize', this.onWindowResize, false);
        renderContainer.addEventListener('mousemove', this.onMouseMove, false);
        renderContainer.addEventListener('touchstart', this.onTouchStart, false);
        renderContainer.addEventListener('mousedown', this.onMouseDown, false);
        renderContainer.addEventListener('keydown', this.onMouseDown, false);
        renderContainer.addEventListener('wheel', this.onMouseWheel, false);
    }

    private assignColorsToFeatureTypes(props: Render3dProps) {
        const featureTypeColors: { [id: number]: string } = {};
        for (let featureType of props.featureTypes) {
            featureTypeColors[featureType.typeid] = this.COLOR_HANDLER.getNewColor();
        }

        this.setState(Object.assign(this.state, {
            featureTypeColors: featureTypeColors
        }));
    }

    private findBoundingBox(props: Render3dProps) {
        this.boundingBox = getBounds(...props.selectedFeatureType.features.map((f) => Render3d.populateCoordinates(f)));
    }

    private setSystemCenter() {
        this.setState({
            systemCenter: [(this.boundingBox.minX + this.boundingBox.maxX)/2, (this.boundingBox.minY + this.boundingBox.maxY)/2]
        });
    }

    private zoomToBoundingBox() {
        const renderAspect = this.renderWidth/this.renderHeight;

        const boxWidth = this.boundingBox.maxX - this.boundingBox.minX;
        const boxHeight = this.boundingBox.maxY - this.boundingBox.minY;

        const distance = Math.abs(Math.max(boxWidth, boxHeight)/Math.sin(this.FOV*(Math.PI/180)/2));

        let correctedDistance = 0;
        // Correct for missing bounding box (by checking isNaN) and aspect of render canvas
        if (!isNaN(distance)) {
            correctedDistance += distance/renderAspect;
        }
        // Correct for Z distance
        if (!isNaN(this.boundingBox.maxZ)) {
            correctedDistance += this.boundingBox.maxZ;
        }
        // Correct for feature types with only one feature, where that feature is a Point and has no bounding box
        correctedDistance += 10;

        this.props.sceneData.controls.reset();
        this.props.sceneData.camera.position.y = correctedDistance;
    }

    /**
     * Must be implemented as an arrow-function to be able to reach 'this'.
     */
    private animateScene = () => {
        if (this.props.sceneData == undefined) {
            return;
        }

        this.props.sceneData.controls.update();

        this.props.sceneData.axisHelper.camera.position.copy(this.props.sceneData.camera.position);
        this.props.sceneData.axisHelper.camera.position.sub(this.props.sceneData.controls.target);
        this.props.sceneData.axisHelper.camera.position.setLength(300);
        this.props.sceneData.axisHelper.camera.lookAt(this.props.sceneData.axisHelper.scene.position);

        this.renderScene();
    };

    private renderScene() {
        // Abort if the scene has not yet been initialized
        if (this.props.sceneData == undefined) {
            return;
        }

        this.raycaster.setFromCamera(this.mouse, this.props.sceneData.camera);

        // Find intersections
        const allInteractableChildren: THREE.Object3D[] = [];
        for (let child of this.props.sceneData.scene.children) {
            if (child.userData['isSelectedFeatureType']) {
                allInteractableChildren.push(child);
            }
        }
        const intersects = this.raycaster.intersectObjects(allInteractableChildren, true);
        if (intersects.length > 0) {
            document.body.style.cursor = 'pointer';

            this.currentIntersected = intersects[0].object;

            this.getIntersectionSphere().visible = true;
            this.getIntersectionSphere().position.copy(intersects[0].point);
        } else {
            document.body.style.cursor = 'auto';

            this.currentIntersected = undefined;

            this.getIntersectionSphere().visible = false;
        }

        // Move ground to camera target if enabled
        if (this.props.ground3dData.follow) {
            const groundObjects = this.props.sceneData.scene.children.filter((c) => c.name === 'grid');
            for (let groundObject of groundObjects) {
                groundObject.position.copy(this.props.sceneData.controls.target);
                groundObject.position.setY(this.props.ground3dData.height);
            }
        }

        // Render the entire scene
        this.props.sceneData.renderer.render(this.props.sceneData.scene, this.props.sceneData.camera);

        this.props.sceneData.axisHelper.renderer.render(this.props.sceneData.axisHelper.scene, this.props.sceneData.axisHelper.camera);
    }

    private getIntersectionSphere(): THREE.Object3D {
        return this.props.sceneData.scene.children.find((c) => c.name === 'intersectionSphere');
    }

    private updateCameraAndRendererToNewCanvasSize() {
        if (this.props.sceneData == undefined) {
            return;
        }

        this.props.sceneData.camera.aspect = this.renderWidth/this.renderHeight;
        this.props.sceneData.camera.updateProjectionMatrix();

        this.props.sceneData.renderer.setSize(this.renderWidth, this.renderHeight);
    }

    private scalePointsByDistance() {
        const scaleVector = new THREE.Vector3();
        const scaleFactor = 100;

        const sprites = this.props.sceneData.scene.children.filter((c) => c.userData.geometryType === 'Point');
        for (let sprite of sprites) {
            const scale = Math.pow(scaleVector.subVectors(sprite.position, this.props.sceneData.camera.position).length(), 0.8)/scaleFactor;
            sprite.scale.set(scale, scale, scale);
        }
    }

    private static populateCoordinates(feature: DisplayFeature): number[][] {
        let coordinates: number[][] = [];

        const geometryData: GeometryObject = WKTParser.parse(feature.geometry.wkt);
        switch (geometryData.type) {
        case 'Point':
            coordinates[0] = geometryData.coordinates;
            break;
        case 'LineString':
            coordinates.push(...geometryData.coordinates);
            break;
        case 'Polygon':
            coordinates.push(...geometryData.coordinates[0]);
            break;
        }

        return coordinates;
    }

    /**
     * Must be implemented as an arrow-function to be able to reach 'this'.
     */
    private onWindowResize = () => {
        if (this.props.sceneData == undefined || this.refs['render-3d'] == undefined) {
            return;
        }

        this.updateInformationAboutCanvas();
        this.updateCameraAndRendererToNewCanvasSize();

        this.scalePointsByDistance();

        requestAnimationFrame(this.animateScene);
    };

    /**
     * Must be implemented as an arrow-function to be able to reach 'this'.
     */
    private onMouseMove = (event: MouseEvent) => {
        event.preventDefault();

        (event.target as HTMLElement).focus();
        this.mouse.x = (event.offsetX/this.renderWidth)*2 - 1;
        this.mouse.y = -(event.offsetY/this.renderHeight)*2 + 1;

        this.scalePointsByDistance();

        requestAnimationFrame(this.animateScene);
    };

    /**
     * Captures touch events on mobile devices and propagates the event to the normal mousedown handler.
     *
     * Must be implemented as an arrow-function to be able to reach 'this'.
     */
    private onTouchStart = (event: MouseEvent) => {
        event.preventDefault();
        this.onMouseDown(event);
    };

    /**
     * Must be implemented as an arrow-function to be able to reach 'this'.
     */
    private onMouseDown = (event: MouseEvent) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();

        if (this.currentIntersected != undefined) {
            const indexOfCurrentIntersected = this.props.selectedFeatures.indexOf(this.currentIntersected.name);
            if (indexOfCurrentIntersected > -1) {
                this.props.deSelectFeatures([this.currentIntersected.name]);
            } else {
                this.props.selectFeatures([this.currentIntersected.name]);
            }
        }

        requestAnimationFrame(this.animateScene);
    };

    /**
     * Must be implemented as an arrow-function to be able to reach 'this'.
     */
    private onMouseWheel = (event: MouseEvent) => {
        this.scalePointsByDistance();
    }

}

const mapStateToProps = (state: FeaturesState) => {
    return {
        featureTypes: state.featureTypes,
        selectedFeatureType: state.selectedFeatureType,
        additionalSelectedFeatureTypes: state.additionalSelectedFeatureTypes,
        selectedFeatures: state.selectedFeatures,
        splitScreenDirection: state.splitScreenDirection,
        sceneData: state.sceneData,
        ground3dData: state.ground3dData
    };
};
const mapRender3dDispatchToProps = ({
    deSelectFeatures: deSelectFeatures,
    selectFeatures: selectFeatures,
    changeSceneData: changeSceneData
});
export default connect(
    mapStateToProps,
    mapRender3dDispatchToProps
)(Render3d);
